import React, { useState, useEffect, useRef } from 'react';

const Knob = ({ value, min = 0, max = 100, onChange, size = 64, label, variant = 'smooth', snap = false }) => {
    const [isDragging, setIsDragging] = useState(false);
    const startY = useRef(0);
    const startValue = useRef(0);

    // Calculate rotation based on value
    // Map value (min-max) to rotation (-135deg to 135deg)
    const range = max - min;
    const rotation = -135 + ((value - min) / range) * 270;

    const handleMouseDown = (e) => {
        setIsDragging(true);
        startY.current = e.clientY;
        startValue.current = value;
        document.body.style.cursor = 'ns-resize';
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;

            const deltaY = startY.current - e.clientY;
            const sensitivity = 0.05; // Sensitivity
            let rawValue = startValue.current + deltaY * sensitivity;

            // Constrain
            rawValue = Math.min(max, Math.max(min, rawValue));

            // Snap Logic
            if (snap) {
                rawValue = Math.round(rawValue);
            }

            if (rawValue !== value) {
                onChange(rawValue);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            document.body.style.cursor = 'default';
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, min, max, onChange, value, snap]);

    // Generate Ticks
    const renderTicks = () => {
        if (variant !== 'tactile') return null;
        const tickCount = Math.floor(max - min) + 1;
        const ticks = [];
        for (let i = 0; i < tickCount; i++) {
            const deg = -135 + (i / (tickCount - 1)) * 270;
            ticks.push(
                <div
                    key={i}
                    className="absolute w-0.5 h-1.5 bg-[#444]"
                    style={{
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${deg}deg) translateY(-${size / 2 + 8}px)`,
                        transformOrigin: 'center center'
                    }}
                />
            );
        }
        return <div className="absolute inset-0 pointer-events-none">{ticks}</div>;
    };

    return (
        <div className="flex flex-col items-center gap-2 relative">
            {renderTicks()}
            <div
                className="relative rounded-full select-none cursor-ns-resize touch-none"
                style={{ width: size, height: size }}
                onMouseDown={handleMouseDown}
            >
                {/* Outer Ring / Bezel */}
                <div className={`absolute inset-0 rounded-full bg-gradient-to-b from-[#e0e0e0] to-[#ffffff] shadow-[0_4px_6px_rgba(0,0,0,0.2),0_1px_2px_rgba(0,0,0,0.1)]`}></div>

                {/* Base Texture */}
                <div className="absolute inset-[2px] rounded-full bg-[#dcdad3] overflow-hidden shadow-inner">
                    {/* Radial sunburst texture for plastic */}
                    <div className="absolute inset-0 opacity-10" style={{
                        background: 'conic-gradient(from 0deg, transparent 0deg, #000 10deg, transparent 20deg)',
                        backgroundSize: '100% 100%'
                    }}></div>
                </div>

                {/* Rotating Cap */}
                <div
                    className="absolute inset-[4px] rounded-full bg-[#eee] shadow-[0_2px_4px_rgba(0,0,0,0.15),inset_0_1px_1px_rgba(255,255,255,0.9)] transform transition-transform duration-75 ease-out"
                    style={{ transform: `rotate(${rotation}deg)` }}
                >
                    {/* Top Surface Matte */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#f8f8f8] to-[#e0e0e0]"></div>

                    {/* Indicator Line */}
                    <div className={`absolute top-1 left-1/2 w-0.5 -ml-[1px] bg-[#e65c00] rounded-full shadow-[0_1px_1px_rgba(255,255,255,0.8)] z-10 ${variant === 'tactile' ? 'h-5' : 'h-3'}`}></div>

                    {/* Center Dimple */}
                    <div className="absolute top-1/2 left-1/2 w-2 h-2 -ml-1 -mt-1 rounded-full bg-gradient-to-br from-[#ddd] to-[#fff] shadow-inner opacity-50"></div>
                </div>
            </div>
            {label && (
                <span className="text-[9px] font-bold uppercase tracking-widest text-[#666] select-none mt-1">
                    {label}
                </span>
            )}
        </div>
    );
};

export default Knob;
